import { Sprout, Leaf, Clock, MapPin, RefreshCw, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from './AdminContext';

export const AdminGrowersTab = () => {
  const { approvedGrowers, growerRegistrations, fixGrowerVisibility, handleGrowerApproval, syncGrowerRegistration, deleteGrower, deleteGrowerRegistration } = useAdmin();

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button variant="outline" size="sm" onClick={fixGrowerVisibility} className="text-yellow-500 border-yellow-500/50"><RefreshCw size={14} className="mr-1" /> Fix Grower Visibility</Button>
      </div>

      {/* Approved */}
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Sprout size={20} /> Active Growers ({approvedGrowers.length})</h2>
      {approvedGrowers.length === 0 ? (
        <Card className="bg-card border-border mb-6"><CardContent className="py-8 text-center"><Leaf size={40} className="mx-auto text-muted-foreground mb-3" /><p className="text-muted-foreground">No active growers yet.</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {approvedGrowers.map(grower => (
            <Card key={grower.id} className="bg-card border-border ring-1 ring-green-500/30">
              <CardHeader className="pb-2 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <CardTitle className="text-base sm:text-lg break-words">{grower.business_name}</CardTitle>
                  <Badge className="self-start sm:self-auto shrink-0 bg-green-500/20 text-green-500">Active</Badge>
                </div>
                <CardDescription className="text-xs sm:text-sm break-all">{grower.owner_name} - {grower.email}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground"><MapPin size={14} className="shrink-0" /><span>{grower.city}, Minnesota</span></div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-xs capitalize">{grower.grow_type}</Badge>
                  {grower.accepts_wholesale && <Badge variant="outline" className="text-xs">Wholesale</Badge>}
                  {grower.offers_clones && <Badge variant="outline" className="text-xs">Clones</Badge>}
                  {grower.offers_seeds && <Badge variant="outline" className="text-xs">Seeds</Badge>}
                  {grower.is_women_owned && <Badge className="text-xs bg-pink-500/20 text-pink-400">Women Owned</Badge>}
                  {grower.is_native_owned && <Badge className="text-xs bg-orange-500/20 text-orange-400">Native Owned</Badge>}
                  {grower.is_bipoc_owned && <Badge className="text-xs bg-purple-500/20 text-purple-400">BIPOC Owned</Badge>}
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{grower.description}</p>
                <div className="flex flex-col gap-2 pt-2">
                  <Button size="sm" variant="outline" className="w-full" asChild><a href={`/grower/${grower.id}`} target="_blank" rel="noopener noreferrer">View Profile</a></Button>
                  <Button size="sm" variant="destructive" className="w-full" onClick={() => deleteGrower(grower.id)}><Trash2 size={14} className="mr-1" /> Delete Grower</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pending */}
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 mt-8"><Clock size={20} /> Pending Registrations ({growerRegistrations.length})</h2>
      {growerRegistrations.length === 0 ? (
        <Card className="bg-card border-border"><CardContent className="py-8 text-center"><Leaf size={40} className="mx-auto text-muted-foreground mb-3" /><p className="text-muted-foreground">No pending grower registrations.</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {growerRegistrations.map(grower => (
            <Card key={grower.id} className={`bg-card border-border ${grower.status === 'pending' ? 'ring-1 ring-yellow-500/30' : ''}`}>
              <CardHeader className="pb-2 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <CardTitle className="text-base sm:text-lg break-words">{grower.business_name}</CardTitle>
                  <Badge className={`self-start sm:self-auto shrink-0 ${grower.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' : grower.status === 'approved' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>{grower.status}</Badge>
                </div>
                <CardDescription className="text-xs sm:text-sm break-all">{grower.owner_name} - {grower.email}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground"><MapPin size={14} className="shrink-0" /><span>{grower.city}, Minnesota</span></div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-xs capitalize">{grower.grow_type}</Badge>
                  {grower.accepts_wholesale && <Badge variant="outline" className="text-xs">Wholesale</Badge>}
                  {grower.offers_clones && <Badge variant="outline" className="text-xs">Clones</Badge>}
                  {grower.offers_seeds && <Badge variant="outline" className="text-xs">Seeds</Badge>}
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{grower.description}</p>
                {grower.specialties && <p className="text-xs text-green-400 break-words">Specialties: {grower.specialties}</p>}
                {grower.phone && <p className="text-xs text-muted-foreground">Phone: {grower.phone}</p>}
                {grower.status === 'pending' && (
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handleGrowerApproval(grower.id, 'approve')}><CheckCircle size={14} className="mr-1" /> Approve</Button>
                    <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleGrowerApproval(grower.id, 'reject')}><XCircle size={14} className="mr-1" /> Reject</Button>
                  </div>
                )}
                {grower.status === 'approved' && grower.grower_id && (
                  <div className="flex flex-col gap-2 pt-2">
                    <Button size="sm" variant="outline" className="w-full" asChild><a href={`/grower/${grower.grower_id}`} target="_blank" rel="noopener noreferrer">View Profile</a></Button>
                    <Button size="sm" variant="destructive" className="w-full" onClick={() => deleteGrower(grower.grower_id)}><Trash2 size={14} className="mr-1" /> Delete Grower</Button>
                  </div>
                )}
                {grower.status === 'approved' && !grower.grower_id && (
                  <div className="flex flex-col gap-2 pt-2">
                    <p className="text-xs text-yellow-500 mb-1">Profile not created - click Sync to fix</p>
                    <Button size="sm" variant="outline" className="w-full bg-yellow-500/20 border-yellow-500/50" onClick={() => syncGrowerRegistration(grower.id)}><RefreshCw size={14} className="mr-1" /> Sync to Growers</Button>
                  </div>
                )}
                {grower.status === 'rejected' && (
                  <Button size="sm" variant="destructive" className="w-full mt-2" onClick={() => deleteGrowerRegistration(grower.id)}><Trash2 size={14} className="mr-1" /> Delete Registration</Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};
